// The spaces @ofself/shell draws. Groups arrive in Paradigm's shape, with the
// photo as `avatar_url`; the shell reads `avatarUrl`, so passing the objects
// through drew every group as letters.
//
// The photo is a URL signed for an hour. The active group is also kept as a
// separate (localStorage-backed) copy, so the shell gets the FRESH entry for it
// from the list, and a periodic re-read updates only the photos in that list.

export const toShellSpace = (group) => ({ ...group, avatarUrl: group.avatar_url || undefined })

export function shellSpaces(groups, activeGroup) {
  const spaces = (groups || []).map(toShellSpace)
  const active = activeGroup
    ? spaces.find(s => s.id === activeGroup.id) || toShellSpace(activeGroup)
    : null
  return { groups: spaces, activeGroup: active }
}

// Newly signed photos onto the groups we hold, touching nothing else. A group
// the fresh list does not mention keeps what it had; one it lists without a
// photo loses its photo.
export function withFreshPhotos(groups, fresh) {
  const byId = new Map((fresh || []).map(g => [g.id, g.avatar_url || null]))
  let changed = false
  const next = (groups || []).map(g => {
    if (!byId.has(g.id) || (g.avatar_url || null) === byId.get(g.id)) return g
    changed = true
    return { ...g, avatar_url: byId.get(g.id) }
  })
  return changed ? next : groups
}

// How long we may hold these photos before re-signing them.
//
// Paradigm states each URL's deadline as `avatar_url_expires_at`, so the refresh is
// scheduled from the server's own number. It used to be a 45-minute constant hardcoded
// against a 60-minute TTL that lives in another repository, with nothing on either side
// testing the relationship — tighten that TTL for good reason and every photo here
// breaks with no edit anywhere near it.
//
// Refresh at 75% of the shortest remaining life: early enough to absorb a slow request
// and a clock a few minutes out of step, late enough not to poll for the sake of it.
export const PHOTO_REFRESH_FALLBACK_MS = 10 * 60 * 1000
const PHOTO_REFRESH_FLOOR_MS = 60 * 1000
const PHOTO_REFRESH_FRACTION = 0.75

export function nextPhotoRefreshMs(groups, now = Date.now()) {
  const deadlines = (groups || [])
    .filter(g => g && g.avatar_url)          // no photo, nothing to re-sign
    .map(g => Date.parse(g.avatar_url_expires_at))
    .filter(t => Number.isFinite(t))
  // No stated deadline anywhere: a server that does not say gets a conservative
  // fixed poll rather than a guess at its TTL.
  if (!deadlines.length) return PHOTO_REFRESH_FALLBACK_MS
  const remaining = Math.min(...deadlines) - now
  return Math.max(PHOTO_REFRESH_FLOOR_MS, remaining * PHOTO_REFRESH_FRACTION)
}
